import * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjs from 'rxjs'

import { SPACE_RECORDS_SELECTION_TARGET } from '../../../constants'
import type { Plugin } from '../plugin'

const SUBMIT_CANCELLATION_REASON = {
  noContext: 'No scenario context available',
  noEmployees: 'No employees to make responsible',
  noSelectedRecords: 'No Space records selected for the bulk update',
  noUpdate: 'No bulk responsible persons update in progress',
} as const

/**
 * Makes the edited employees responsible for every selected record.
 */
export const executeSubmit = store.effects.create(
  (feature: Plugin['feature'], plugin: Plugin) =>
    feature.actions.submit.request.observe().pipe(
      rxjs.withLatestFrom(
        feature.selectors.isUpdating.asObservable,
        feature.selectors.employeeIds.asObservable,
        feature.selectors.primaryEmployeeId.asObservable,
        plugin.collection.feature.selectors.context.asObservable,
        plugin.collection.plugins.selectionPlugin.feature.selectors.getAllSelectedIds(
          {
            target: SPACE_RECORDS_SELECTION_TARGET,
          },
        ).asObservable,
      ),
      rxjs.mergeMap(
        ([
          action,
          isUpdating,
          employeeIds,
          primaryEmployeeId,
          context,
          selectedIds,
        ]) => {
          if (!isUpdating) {
            return rxjs.of(
              feature.actions.submit.cancellation
                .from(action)
                .create({ reason: SUBMIT_CANCELLATION_REASON.noUpdate }),
            )
          }

          if (!ents.core.admin.Context.ForScenario.isForScenario(context)) {
            return rxjs.of(
              feature.actions.submit.cancellation
                .from(action)
                .create({ reason: SUBMIT_CANCELLATION_REASON.noContext }),
            )
          }

          if (!employeeIds.length) {
            return rxjs.of(
              feature.actions.submit.cancellation
                .from(action)
                .create({ reason: SUBMIT_CANCELLATION_REASON.noEmployees }),
            )
          }

          const spaceRecordIds = selectedIds.filter(
            ents.extra.space.Record.Id.isId,
          )

          if (!spaceRecordIds.length) {
            return rxjs.of(
              feature.actions.submit.cancellation.from(action).create({
                reason: SUBMIT_CANCELLATION_REASON.noSelectedRecords,
              }),
            )
          }

          return plugin.api
            .update({
              context: { ...context, spaceRecordIds },
              employeeIds,
              primaryEmployeeId,
            })
            .pipe(
              rxjs.map(() =>
                feature.actions.submit.success.from(action).create(),
              ),
              rxjs.catchError((error: Error) =>
                rxjs.of(
                  feature.actions.submit.error.from(action).create({ error }),
                ),
              ),
            )
        },
      ),
    ),
)
